import React, { useState } from 'react';

/**
 * ResponsiveImage component
 * Displays a responsive image with smooth loading animation and placeholder
 *
 * Props:
 *  - src: image URL (required)
 *  - alt: alt text (required)
 *  - className: CSS classes to apply to image (optional)
 *  - containerClassName: CSS classes to apply to container (optional)
 *  - placeholderClassName: CSS classes for placeholder (optional)
 *  - aspectRatio: aspect ratio class like "aspect-square" (optional)
 *  - lazy: enable lazy loading (default: true)
 *  - onLoad: callback when image loads (optional)
 *  - fillContainer: whether image fills the container (default: false)
 */
export const ResponsiveImage = ({
    src,
    alt,
    className = '',
    containerClassName = '',
    placeholderClassName = 'bg-gradient-to-br from-surface-muted to-mekomit-50',
    aspectRatio = 'aspect-square',
    lazy = true,
    onLoad,
    fillContainer = false,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    const handleImageLoad = () => {
        setIsLoading(false);
        if (onLoad) onLoad();
    };

    const handleImageError = () => {
        setError(true);
        setIsLoading(false);
    };

    const containerClasses = fillContainer
        ? `relative overflow-hidden w-full h-full ${containerClassName}`
        : `relative overflow-hidden ${aspectRatio} ${containerClassName}`;

    return (
        <div className={containerClasses}>
            {/* Placeholder shimmer - visible only while loading */}
            {isLoading && (
                <div
                    className={`absolute inset-0 ${placeholderClassName} animate-pulse`}
                    aria-hidden="true"
                />
            )}

            {/* Main image */}
            {!error ? (
                <img
                    src={src}
                    alt={alt}
                    loading={lazy ? 'lazy' : 'eager'}
                    className={`w-full h-full object-cover transition-opacity duration-500 ${
                        isLoading ? 'opacity-0' : 'opacity-100 animate-image-fade-in'
                    } ${className}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
            ) : (
                /* Fallback when image fails to load */
                <div className={`absolute inset-0 flex items-center justify-center ${placeholderClassName}`}>
                    <div className="text-center">
                        <svg className="w-8 h-8 text-mekomit-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs text-mekomit-500">لا ניתן לטעון תמונה</p>
                    </div>
                </div>
            )}

            {/* Loading spinner overlay - visible while image is loading */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-10">
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResponsiveImage;
